import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Field, Icon, IconButton, Segmented, TextArea, useLayout, useToast } from "@agentsaas/ui";
import { ApiError, type PullRequest } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime } from "../app/format";
import { ck, putPull, usePull, usePullDiff, useTasks } from "../data/collab";
import { Loading } from "../layout/Loading";
import { Markdown } from "../chat/Markdown";
import { DiffView } from "../files/CodeEditor";
import { CollabFrame } from "../project/ProjectChrome";
import { NotFound } from "../pages/NotFound";
import { TaskStatusBadge } from "../tasks/TasksPage";
import { PrStateBadge, VerdictBadge, useActorName } from "./PullsPage";
import { parsePatch, toSides } from "./diff";

type DiffFile = NonNullable<ReturnType<typeof usePullDiff>["data"]>[number];
type MergeMethod = "squash" | "merge" | "rebase";
const METHODS: MergeMethod[] = ["squash", "merge", "rebase"];

/** /p/:pid/pulls/:rid/:num：PR 详情、按文件 diff（桌面 Monaco diff，手机统一 diff）、评审、合并 */
export function PullDetailPage() {
  const { pid = "", rid = "", num = "0" } = useParams();
  return (
    <CollabFrame pid={pid} section="pulls">
      <PullDetail pid={pid} rid={rid} num={Number(num)} />
    </CollabFrame>
  );
}

function PullDetail({ pid, rid, num }: { pid: string; rid: string; num: number }) {
  const { t, locale } = useT();
  const mobile = useLayout() === "mobile";
  const navigate = useNavigate();
  const tz = useSession().state.user?.timezone;
  const pr = usePull(rid, num);
  const diff = usePullDiff(rid, num);
  const tasks = useTasks(pid);
  const who = useActorName(pid);
  if (pr.isPending) return <Loading />;
  if (pr.isError || !pr.data) return <NotFound />;
  const p = pr.data;
  const task = tasks.data?.find((x) => x.id === p.taskId);
  return (
    <div className="page pr-detail" data-testid="pr-detail" data-state={p.state}>
      <div className="task-detail__back">
        {mobile ? (
          <IconButton icon="back" label={t("common.back")} onClick={() => navigate(`/p/${pid}/pulls`)} data-testid="pr-back" />
        ) : (
          <Link to={`/p/${pid}/pulls`} className="small">
            {t("pr.back_to_list")}
          </Link>
        )}
      </div>
      <header className="task-detail__head">
        <h1 className="page__title">
          {p.title} <span className="muted">{t("pr.number", { n: p.number })}</span>
        </h1>
        <div className="row small">
          <PrStateBadge pr={p} />
          <VerdictBadge pr={p} />
          <span className="muted">{t("pr.opened_by", { name: who(p.author) })}</span>
          <code className="break row-inline">
            {p.head}
            <Icon name="chevronRight" size={12} />
            {p.base}
          </code>
          <span className="diffstat">
            <span className="diffstat__add">{t("pr.additions", { n: p.additions })}</span> <span className="diffstat__del">{t("pr.deletions", { n: p.deletions })}</span>
          </span>
        </div>
        {task ? (
          <div className="small">
            {t("pr.task_label")}
            <Link to={`/p/${pid}/tasks/${task.id}`} data-testid="pr-task-link">
              {task.title}
            </Link>{" "}
            <TaskStatusBadge task={task} />
          </div>
        ) : null}
      </header>

      <div className="pr-detail__top">
        <section className="card stack">
          {p.body ? <Markdown text={p.body} /> : <p className="muted small">{t("pr.no_body")}</p>}
          {p.reviews.length ? (
            <ol className="reviews" data-testid="pr-reviews">
              {p.reviews.map((r, i) => (
                <li key={i} className={`review review--${r.event}`}>
                  <div className="row small">
                    <strong>{who(r.by)}</strong>
                    <Badge tone={r.event === "approve" ? "success" : r.event === "request_changes" ? "warning" : "neutral"}>{t(`pr.event.${r.event}`)}</Badge>
                    <time className="muted" dateTime={r.at}>
                      {formatDateTime(r.at, locale, tz)}
                    </time>
                  </div>
                  {r.body ? <p className="review__body">{r.body}</p> : null}
                </li>
              ))}
            </ol>
          ) : null}
        </section>
        {p.state === "open" ? <ReviewBox pid={pid} pr={p} /> : null}
      </div>

      <section className="pr-files" aria-label={t("pr.files")}>
        <h2 className="section-title">
          {t("pr.files")} {diff.data ? <Badge>{diff.data.length}</Badge> : null}
        </h2>
        {diff.isPending ? <Loading /> : !diff.data?.length ? <EmptyState title={t("pr.no_files")} /> : mobile ? <MobileFiles files={diff.data} /> : <DesktopFiles files={diff.data} />}
      </section>
    </div>
  );
}

function FileStat({ f }: { f: DiffFile }) {
  const { t } = useT();
  return (
    <>
      <Badge tone={f.status === "added" ? "success" : f.status === "deleted" ? "danger" : "neutral"}>{t(`pr.file.${f.status}`)}</Badge>
      <span className="diffstat small">
        <span className="diffstat__add">{t("pr.additions", { n: f.additions })}</span> <span className="diffstat__del">{t("pr.deletions", { n: f.deletions })}</span>
      </span>
    </>
  );
}

/** 桌面：左侧文件列表，右侧 Monaco diff（只读） */
function DesktopFiles({ files }: { files: DiffFile[] }) {
  const { t } = useT();
  const [sel, setSel] = useState(0);
  const [inline, setInline] = useState(false);
  const f = files[Math.min(sel, files.length - 1)]!;
  const sides = toSides(f.patch);
  return (
    <div className="pr-split">
      <ul className="pr-filelist" role="listbox" aria-label={t("pr.files")}>
        {files.map((x, i) => (
          <li key={x.path} role="option" aria-selected={i === sel}>
            <button type="button" className={`pr-file${i === sel ? " is-active" : ""}`} onClick={() => setSel(i)} data-testid="pr-file">
              <span className="ellipsis pr-file__path" title={x.path}>
                {x.path}
              </span>
              <FileStat f={x} />
            </button>
          </li>
        ))}
      </ul>
      <div className="pr-diff">
        <div className="pr-diff__bar">
          <code className="ellipsis grow">{f.path}</code>
          <Segmented<"split" | "inline">
            label={t("pr.diff_mode")}
            value={inline ? "inline" : "split"}
            onChange={(v) => setInline(v === "inline")}
            options={[
              { value: "split", label: t("pr.diff_split") },
              { value: "inline", label: t("pr.diff_inline") },
            ]}
          />
        </div>
        <div className="pr-diff__editor" data-testid="pr-diff">
          {f.patch === null ? <EmptyState title={t("pr.binary")} /> : <DiffView key={f.path} path={f.path} original={sides.original} modified={sides.modified} readOnly inline={inline} />}
        </div>
      </div>
    </div>
  );
}

/** 手机：每个文件一个可折叠的统一 diff 文本视图 */
function MobileFiles({ files }: { files: DiffFile[] }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(files.slice(0, 1).map((f) => f.path)));
  const toggle = (p: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(p)) n.delete(p);
      else n.add(p);
      return n;
    });
  return (
    <ul className="pr-mfiles">
      {files.map((f) => (
        <li key={f.path} className="card pr-mfile">
          <button type="button" className="pr-mfile__head" aria-expanded={open.has(f.path)} onClick={() => toggle(f.path)} data-testid="pr-file">
            <Icon name={open.has(f.path) ? "chevronDown" : "chevronRight"} size={16} />
            <span className="grow ellipsis pr-file__path">{f.path}</span>
            <FileStat f={f} />
          </button>
          {open.has(f.path) ? <UnifiedDiff patch={f.patch} /> : null}
        </li>
      ))}
    </ul>
  );
}

export function UnifiedDiff({ patch }: { patch: string | null }) {
  const { t } = useT();
  const lines = parsePatch(patch);
  if (!lines.length) return <p className="muted small">{t("pr.binary")}</p>;
  return (
    <div className="udiff" data-testid="unified-diff" role="table" aria-label={t("pr.diff")}>
      {lines.map((l, i) =>
        l.kind === "hunk" ? (
          <div key={i} className="udiff__hunk" role="row">
            <span role="cell">{l.text}</span>
          </div>
        ) : (
          <div key={i} className={`udiff__line udiff__line--${l.kind}`} role="row">
            <span className="udiff__no" role="cell">
              {l.oldNo ?? ""}
            </span>
            <span className="udiff__no" role="cell">
              {l.newNo ?? ""}
            </span>
            <span className="udiff__sign" role="cell" aria-hidden="true">
              {l.kind === "add" ? "+" : l.kind === "del" ? "-" : " "}
            </span>
            <span className="udiff__text" role="cell">
              {l.text}
            </span>
          </div>
        ),
      )}
    </div>
  );
}

/** 评审（通过 / 要求修改 / 评论）与合并 */
function ReviewBox({ pid, pr }: { pid: string; pr: PullRequest }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [method, setMethod] = useState<MergeMethod>("squash");
  const params = { rid: pr.repoId, num: pr.number };

  const review = async (event: "approve" | "request_changes" | "comment") => {
    setBusy(event);
    try {
      putPull(qc, pid, await api.call("pullReview", { params, body: { event, body } }));
      setBody("");
      toast.show({ message: t(`pr.reviewed.${event}`), tone: "success", duration: 2000 });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setBusy(null);
    }
  };
  const merge = async () => {
    setBusy("merge");
    try {
      putPull(qc, pid, await api.call("pullMerge", { params, body: { method } }));
      toast.show({ message: t("pr.merged_toast", { n: pr.number }), tone: "success" });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      if (e instanceof ApiError && e.code === "git.not_mergeable") void qc.invalidateQueries({ queryKey: ck.pull(pr.repoId, pr.number) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card stack pr-review" aria-label={t("pr.review")} data-testid="pr-review">
      <Field label={t("pr.review")}>
        {(id) => <TextArea id={id} rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("pr.review_placeholder")} data-testid="pr-review-body" />}
      </Field>
      <div className="row pr-review__buttons">
        <Button size="sm" loading={busy === "comment"} disabled={!!busy || !body.trim()} onClick={() => void review("comment")} data-testid="pr-comment">
          {t("pr.event.comment")}
        </Button>
        <Button size="sm" loading={busy === "request_changes"} disabled={!!busy} onClick={() => void review("request_changes")} data-testid="pr-request-changes">
          {t("pr.event.request_changes")}
        </Button>
        <Button size="sm" variant="primary" icon="check" loading={busy === "approve"} disabled={!!busy} onClick={() => void review("approve")} data-testid="pr-approve">
          {t("pr.event.approve")}
        </Button>
      </div>
      <hr className="divider" />
      <div className="pr-merge">
        <Segmented<MergeMethod>
          label={t("pr.merge_method")}
          value={method}
          onChange={setMethod}
          options={METHODS.map((m) => ({ value: m, label: t(`pr.method.${m}`) }))}
        />
        <Button variant="primary" icon="merge" loading={busy === "merge"} disabled={!!busy || pr.mergeable === false} onClick={() => void merge()} data-testid="pr-merge">
          {t("pr.merge")}
        </Button>
      </div>
      {pr.mergeable === false ? <p className="small form-error">{t("error.git.not_mergeable")}</p> : null}
    </section>
  );
}
